// Files in and out.
//
// A MINDMAP FILE IS ONE .svg: the picture of the whole map (so Obsidian,
// Notion, Dropbox previews and any browser show it as it is), with the map's
// JSON saved inside it:
//   <metadata><cth-mindmap xmlns="https://mindmaps.coachtonyhockey.com/format/1" version="1">{...}</cth-mindmap></metadata>
// The JSON is XML-escaped text (only & < > are escaped), so it stays readable
// for Claude Code or anyone opening the file. Never change the element name or
// namespace; the JSON follows engine/model.js (additive only).
//
// Also: Markdown / plain text / OPML outlines in and out, and PNG pictures.
import { blankMap, node, normalize, walkAll } from './model.js';
import { layoutMap } from './layout.js';
import { svgDocument } from './render.js';

export const NS = 'https://mindmaps.coachtonyhockey.com/format/1';
const escXml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const unescXml = (s) => String(s)
  .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'")
  .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
  .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
  .replace(/&amp;/g, '&');

/** The file's text for a map: the picture with the map inside. */
export function mapToSVG(map, { transparent = false, title = '' } = {}) {
  const L = layoutMap(map);
  const meta = `<metadata><cth-mindmap xmlns="${NS}" version="1">${escXml(JSON.stringify(map))}</cth-mindmap></metadata>`;
  return svgDocument(map, L, { metadata: meta, transparent, title: title || map.root.text });
}

/** The map saved in an .svg file's text, or null when it is not a CTH mindmap. */
export function readMindmapSVG(text) {
  const m = String(text || '').match(/<cth-mindmap\b[^>]*>([\s\S]*?)<\/cth-mindmap>/);
  if (!m) return null;
  try { return normalize(JSON.parse(unescXml(m[1]))); } catch (_) { return null; }
}

/** Just the picture (no map inside), for exports. */
export function pictureSVG(map, { transparent = false, focus = null } = {}) {
  return svgDocument(map, layoutMap(map, { focus }), { transparent, title: map.root.text });
}

/** An SVG string as a PNG blob, `scale` times its size (kept inside what browsers can draw). */
export async function svgToPng(svg, scale = 2) {
  const w = Number((svg.match(/width="(\d+)"/) || [])[1]) || 800;
  const h = Number((svg.match(/height="(\d+)"/) || [])[1]) || 600;
  let k = scale;
  k = Math.min(k, 16000 / w, 16000 / h, Math.sqrt(16e6 / (w * h)));
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.decoding = 'sync';
    await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('The picture could not be drawn')); img.src = url; });
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
    const g = c.getContext('2d');
    g.drawImage(img, 0, 0, c.width, c.height);
    return await new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error('The picture could not be drawn'))), 'image/png'));
  } finally { URL.revokeObjectURL(url); }
}

// ---- outlines --------------------------------------------------------------------------

function itemFrom(raw) {
  let text = raw.trim();
  const extra = {};
  const t = text.match(/^\[( |x|X)\]\s+(.*)$/);
  if (t) { extra.task = t[1] === ' ' ? 'todo' : 'done'; text = t[2]; }
  const l = text.match(/^\[([^\]]+)\]\((\S+?)\)$/);
  if (l) { text = l[1]; extra.link = l[2]; }
  const b = text.match(/^\*\*(.+)\*\*$/) || text.match(/^__(.+)__$/);
  if (b) { text = b[1]; extra.bold = true; }
  return node(text, extra);
}

/**
 * A map from an outline: Markdown (headings, bullet and numbered lists, [ ]
 * tasks, [text](url) links, > quotes as notes), or any indented text (tabs or
 * spaces, as pasted from Notion, Workflowy, a note). One top item becomes the
 * central topic; several become branches of a central topic called `title`.
 */
export function outlineToMap(text, title = 'Central idea', opts = {}) {
  const top = { children: [] };
  const heads = [{ level: 0, node: top }];
  let list = [];
  let last = null;
  let inCode = false;
  for (const rawLine of String(text || '').replace(/\r\n?/g, '\n').split('\n')) {
    if (/^\s*```/.test(rawLine)) { inCode = !inCode; continue; }
    if (inCode || !rawLine.trim() || /^\s*(---+|\*\*\*+)\s*$/.test(rawLine)) continue;
    const line = rawLine.replace(/\t/g, '    ');
    const hm = line.match(/^(#{1,6})\s+(.*)$/);
    if (hm) {
      const level = hm[1].length;
      while (heads.length > 1 && heads[heads.length - 1].level >= level) heads.pop();
      const nd = itemFrom(hm[2]);
      heads[heads.length - 1].node.children.push(nd);
      heads.push({ level, node: nd });
      list = []; last = nd;
      continue;
    }
    const qm = line.match(/^\s*>\s?(.*)$/);
    if (qm) { if (last) last.note = last.note ? `${last.note}\n${qm[1]}` : qm[1]; continue; }
    const bm = line.match(/^(\s*)(?:[-*+]|\d+[.)])\s+(.*)$/);
    const indent = (bm ? bm[1] : line.match(/^(\s*)/)[1]).length;
    const body = bm ? bm[2] : line.trim();
    while (list.length && list[list.length - 1].indent >= indent) list.pop();
    const parent = list.length ? list[list.length - 1].node : heads[heads.length - 1].node;
    const nd = itemFrom(body);
    parent.children.push(nd);
    list.push({ indent, node: nd });
    last = nd;
  }
  const map = blankMap(title, opts);
  if (top.children.length === 1) map.root = top.children[0];
  else if (top.children.length > 1) map.root.children = top.children;
  return normalize(map);
}

/** Topics from pasted text, to add under a topic (not a whole map). */
export function outlineToBranches(text) {
  const top = outlineToMap(text, '\u0000');
  return top.root.text === '\u0000' ? top.root.children : [top.root];
}

const mdItem = (nd) => {
  let t = (nd.text || '').replace(/\n+/g, ' ');
  if (nd.bold) t = `**${t}**`;
  if (nd.link) t = `[${t || nd.link}](${nd.link})`;
  if (nd.task) t = `[${nd.task === 'done' ? 'x' : ' '}] ${t}`;
  return t;
};
/** The map as a Markdown outline: the central topic a heading, everything else nested bullets. */
export function mapToMarkdown(map) {
  const out = [];
  const walk = (nd, depth) => {
    const pad = '  '.repeat(depth);
    out.push(`${pad}- ${mdItem(nd)}`);
    if (nd.note) for (const l of nd.note.split('\n')) out.push(`${pad}  > ${l}`);
    for (const c of nd.children) walk(c, depth + 1);
  };
  for (const t of [map.root, ...map.floating]) {
    if (out.length) out.push('');
    out.push(`# ${mdItem(t)}`);
    if (t.note) for (const l of t.note.split('\n')) out.push(`> ${l}`);
    if (t.children.length) out.push('');
    for (const c of t.children) walk(c, 0);
  }
  return `${out.join('\n')}\n`;
}
/** Plain indented text (tabs), as most outliners paste it. */
export function mapToText(map, from = null) {
  const out = [];
  const walk = (nd, depth) => { out.push(`${'\t'.repeat(depth)}${(nd.text || '').replace(/\n+/g, ' ')}`); for (const c of nd.children) walk(c, depth + 1); };
  if (from) walk(from, 0); else for (const t of [map.root, ...map.floating]) walk(t, 0);
  return out.join('\n');
}

const attr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '&#10;');
export function mapToOPML(map) {
  const walk = (nd, pad) => {
    const a = `text="${attr(nd.text)}"${nd.note ? ` _note="${attr(nd.note)}"` : ''}${nd.link ? ` url="${attr(nd.link)}"` : ''}${nd.task === 'done' ? ' _complete="true"' : ''}`;
    if (!nd.children.length) return `${pad}<outline ${a}/>`;
    return `${pad}<outline ${a}>\n${nd.children.map((c) => walk(c, `${pad}  `)).join('\n')}\n${pad}</outline>`;
  };
  return `<?xml version="1.0" encoding="UTF-8"?>\n<opml version="2.0">\n  <head><title>${attr(map.root.text)}</title></head>\n  <body>\n${[map.root, ...map.floating].map((t) => walk(t, '    ')).join('\n')}\n  </body>\n</opml>\n`;
}
export function opmlToMap(text, title = 'Central idea') {
  const doc = new DOMParser().parseFromString(String(text), 'text/xml');
  const body = doc.querySelector('body');
  if (!body) throw new Error('That is not an OPML outline.');
  const conv = (el) => {
    const nd = node(el.getAttribute('text') || el.getAttribute('title') || '');
    const note = el.getAttribute('_note'); if (note) nd.note = note;
    const url = el.getAttribute('url') || el.getAttribute('htmlUrl'); if (url) nd.link = url;
    if (el.getAttribute('_complete') === 'true') nd.task = 'done';
    nd.children = [...el.children].filter((c) => c.tagName === 'outline').map(conv);
    return nd;
  };
  const tops = [...body.children].filter((c) => c.tagName === 'outline').map(conv);
  const map = blankMap(doc.querySelector('head > title')?.textContent || title);
  if (tops.length === 1) map.root = tops[0]; else map.root.children = tops;
  return normalize(map);
}

/** Any file Tony might import, as a map. */
export function importText(name, text) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  const base = name.replace(/\.[^.]+$/, '');
  if (ext === 'svg') { const m = readMindmapSVG(text); if (!m) throw new Error('This SVG is not a CTH mindmap.'); return m; }
  if (ext === 'opml') return opmlToMap(text, base);
  if (ext === 'json') { const j = JSON.parse(text); if (j && j.root) return normalize(j); throw new Error('This JSON is not a CTH mindmap.'); }
  return outlineToMap(text, base);
}

export const wordCount = (map) => { let k = 0; for (const nd of walkAll(map)) k += (nd.text.match(/\S+/g) || []).length; return k; };
